// เหตุฉุกเฉิน / นับยอดนักเรียน — ประกาศเหตุ แล้วนับคนที่จุดรวมพลจนเหลือ "ยังไม่พบ" เป็นศูนย์
//
// ทุกอย่างบนจอจัดให้เห็นตัวเลข "ยังไม่พบ" กับรายชื่อของมันก่อนอย่างอื่น ครูที่จุดรวมพล
// แตะบัตรนักเรียน (มือถือ Android) หรือกดชื่อ หรือกด "ทั้งหลังมาครบ" ก็ได้ — สามทางนี้ถูกบันทึก
// แยกกัน เพื่อให้ตอบได้หลังเหตุว่า "ปลอดภัย" ได้มาจากการเห็นตัวจริงหรือจากการกดเหมา
//
// คนที่ระบบตัดออกจากยอด (กลับบ้าน/ออกนอกที่ประตูมีบันทึก, ห้องพยาบาล) แสดงแยก ส่วนคนที่
// ระบบแค่สงสัย ยังอยู่ใน "ยังไม่พบ" พร้อมป้ายเหตุผล (ดูหัว server/models/emergencyModel.js)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, ListGroup, Row, Spinner, Table } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import io from 'socket.io-client';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import useNfcReader, { nfcUnavailableReason } from '../utils/useNfcReader';
import { toThaiDateLong } from '../utils/thaiDate';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';
const UPLOAD_BASE = API.replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);
const apiError = (error) => error?.response?.data?.message || 'ลองใหม่อีกครั้ง';

const TYPES = [
  { key: 'fire', label: 'ไฟไหม้', icon: 'ri-fire-line' },
  { key: 'evacuation', label: 'อพยพ', icon: 'ri-run-line' },
  { key: 'missing', label: 'นักเรียนหาย', icon: 'ri-user-search-line' },
  { key: 'drill', label: 'ซ้อมอพยพ', icon: 'ri-shield-check-line' },
  { key: 'other', label: 'เหตุอื่น', icon: 'ri-alarm-warning-line' },
];

const FILTERS = [
  { key: 'waiting', label: 'ยังไม่พบ' },
  { key: 'safe', label: 'ปลอดภัย' },
  { key: 'excused', label: 'ไม่อยู่ในหอ' },
  { key: 'all', label: 'ทั้งหมด' },
];

const toDate = (value) => (value ? new Date(String(value).replace(' ', 'T')) : null);
const clock = (value) => {
  const d = toDate(value);
  return d && !Number.isNaN(d.getTime()) ? d.toTimeString().slice(0, 5) : '';
};
const minutesSince = (value) => {
  const d = toDate(value);
  return d ? Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000)) : 0;
};

const Avatar = ({ photo, name, size = 36 }) => {
  const src = photoUrl(photo);
  return src ? (
    <img src={src} alt={name} width={size} height={size} className="rounded-circle flex-shrink-0" style={{ objectFit: 'cover' }} />
  ) : (
    <span className="rounded-circle bg-secondary bg-opacity-25 d-inline-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: size, height: size }}>
      <i className="ri-user-line text-secondary"></i>
    </span>
  );
};

/* ── ประกาศเหตุ ───────────────────────────────────────────────────────────── */

const DeclareForm = ({ onStarted }) => {
  const [villages, setVillages] = useState([]);
  const [type, setType] = useState('drill');
  const [title, setTitle] = useState('');
  const [assembly, setAssembly] = useState('');
  const [whole, setWhole] = useState(true);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosInstance.get(`${API}/safety/emergency/villages`).then(({ data }) => setVillages(data)).catch(() => {});
  }, []);

  const toggle = (id) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const count = whole
    ? villages.reduce((n, v) => n + v.students, 0)
    : villages.filter((v) => picked.includes(v.village_id)).reduce((n, v) => n + v.students, 0);

  const submit = async () => {
    if (!whole && !picked.length) { Swal.fire('ยังไม่ได้เลือกเขต', '', 'warning'); return; }
    const label = TYPES.find((t) => t.key === type).label;
    const ok = await Swal.fire({
      icon: type === 'drill' ? 'question' : 'warning',
      title: `ประกาศ${label}?`,
      html: `แจ้งครูหอพัก ฝ่ายปกครอง พยาบาล และผู้บริหารทันที<br/>นับยอด ${count.toLocaleString()} คน`,
      showCancelButton: true,
      confirmButtonText: 'ประกาศ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    });
    if (!ok.isConfirmed) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.post(`${API}/safety/emergency`, {
        type, title: title.trim() || null, assembly: assembly.trim(), village_ids: whole ? null : picked,
      });
      onStarted(data.ev_id);
    } catch (e) {
      Swal.fire('ประกาศไม่สำเร็จ', apiError(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="card-one mb-3 border-danger">
      <Card.Header><Card.Title as="h6" className="mb-0 text-danger"><i className="ri-alarm-warning-line me-1"></i>ประกาศเหตุ</Card.Title></Card.Header>
      <Card.Body>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {TYPES.map((t) => (
            <Button key={t.key} variant={type === t.key ? 'danger' : 'outline-danger'} onClick={() => setType(t.key)}>
              <i className={`${t.icon} me-1`}></i>{t.label}
            </Button>
          ))}
        </div>
        <Row className="g-2 mb-3">
          <Col md={6}>
            <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} placeholder="หัวข้อ (ไม่บังคับ) เช่น ไฟไหม้หลัง 3/13" />
          </Col>
          <Col md={6}>
            <Form.Control value={assembly} onChange={(e) => setAssembly(e.target.value)} placeholder="จุดรวมพล เช่น สนามฟุตบอล" />
          </Col>
        </Row>
        <Form.Check type="switch" id="whole-school" label="ทั้งโรงเรียน" checked={whole} onChange={(e) => setWhole(e.target.checked)} className="mb-2" />
        {!whole && (
          <div className="d-flex flex-wrap gap-1 mb-3">
            {villages.map((v) => (
              <Button key={v.village_id} size="sm"
                variant={picked.includes(v.village_id) ? (v.village_gender === 'm' ? 'primary' : 'danger') : 'outline-secondary'}
                onClick={() => toggle(v.village_id)}>
                {v.village_name} {v.village_gender === 'm' ? 'ช' : 'ญ'} <span className="opacity-75">({v.students})</span>
              </Button>
            ))}
          </div>
        )}
        <Button variant="danger" size="lg" className="w-100" disabled={saving} onClick={submit}>
          {saving ? <Spinner size="sm" animation="border" /> : `ประกาศ · นับยอด ${count.toLocaleString()} คน`}
        </Button>
      </Card.Body>
    </Card>
  );
};

const History = ({ onOpen }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axiosInstance.get(`${API}/safety/emergency`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);
  if (!rows) return null;
  return (
    <Card className="card-one">
      <Card.Header><Card.Title as="h6" className="mb-0">เหตุที่ผ่านมา</Card.Title></Card.Header>
      <Card.Body className="p-0">
        <Table responsive hover className="mb-0 fs-sm">
          <thead>
            <tr><th>วันที่</th><th>เหตุ</th><th>ขอบเขต</th><th className="text-end">นับยอด</th><th className="text-end">ยังไม่พบตอนปิด</th><th>นับครบใน</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-secondary py-3">ยังไม่เคยมีเหตุ</td></tr>
            ) : rows.map((r) => {
              const mins = r.last_safe_at ? Math.round((toDate(r.last_safe_at) - toDate(r.ev_started_at)) / 60000) : null;
              return (
                <tr key={r.ev_id} role="button" onClick={() => onOpen(r.ev_id)}>
                  <td>{toThaiDateLong(r.ev_started_at, { short: true })} {clock(r.ev_started_at)}</td>
                  <td>{r.type_th} · {r.ev_title}</td>
                  <td>{r.ev_scope_label}</td>
                  <td className="text-end">{r.ev_total ?? '-'}</td>
                  <td className="text-end">{r.ev_status === 'active' ? <Badge bg="danger">กำลังดำเนินการ</Badge> : (r.ev_missing ? <span className="text-danger fw-semibold">{r.ev_missing}</span> : 0)}</td>
                  <td>{mins !== null ? `${mins} นาที` : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
      <Card.Footer className="fs-xs text-secondary">"นับครบใน" = จากประกาศเหตุถึงการยืนยันคนสุดท้าย — ใช้เทียบผลการซ้อมแต่ละครั้ง</Card.Footer>
    </Card>
  );
};

/* ── ระหว่างเหตุ ──────────────────────────────────────────────────────────── */

const Counter = ({ value, label, variant, big }) => (
  <div className={`text-center p-2 rounded bg-${variant} bg-opacity-10 flex-fill`}>
    <div className={`fw-bold text-${variant}`} style={{ fontSize: big ? 44 : 24, lineHeight: 1.1 }}>{value.toLocaleString()}</div>
    <div className="fs-sm">{label}</div>
  </div>
);

const Board = ({ evId, onBack }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('waiting');
  const [village, setVillage] = useState(null);
  const [q, setQ] = useState('');
  const [lastScan, setLastScan] = useState(null);
  const [, tick] = useState(0);
  const reloadTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await axiosInstance.get(`${API}/safety/emergency/${evId}`);
      setData(res);
    } catch (e) {
      Swal.fire('อ่านข้อมูลไม่สำเร็จ', apiError(e), 'error');
    }
  }, [evId]);

  useEffect(() => { load(); }, [load]);

  // ครูหลายสิบคนกดพร้อมกัน — รวมการโหลดใหม่ให้เหลือครั้งเดียวต่อครึ่งวินาที
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } });
    socket.on('emergency:update', (p) => {
      if (Number(p?.ev_id) !== Number(evId)) return;
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(load, 500);
    });
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => { socket.disconnect(); clearInterval(t); clearTimeout(reloadTimer.current); };
  }, [evId, load]);

  const active = data?.event.ev_status === 'active';
  const canMark = active && data?.access.mark;

  const setLocal = (stIds, status, extra = {}) => setData((prev) => ({
    ...prev,
    students: prev.students.map((s) => (stIds.includes(s.st_id) ? { ...s, status, ...extra } : s)),
  }));

  const mark = async (stIds, status, reason) => {
    const before = data;
    setLocal(stIds, status, reason ? { reason } : {});
    try {
      await axiosInstance.post(`${API}/safety/emergency/${evId}/mark`, { st_ids: stIds, status, reason });
    } catch (e) {
      setData(before);
      Swal.fire('บันทึกไม่สำเร็จ', apiError(e), 'error');
    }
  };

  const markAway = async (s) => {
    const { isConfirmed, value } = await Swal.fire({
      title: `${s.st_name} ไม่อยู่ในหอ`,
      input: 'text',
      inputPlaceholder: 'อยู่ที่ไหน เช่น ผู้ปกครองรับกลับเมื่อวาน',
      inputValidator: (v) => (!v?.trim() ? 'ต้องระบุว่าอยู่ที่ไหน' : undefined),
      showCancelButton: true, confirmButtonText: 'บันทึก', cancelButtonText: 'ยกเลิก',
    });
    if (isConfirmed) mark([s.st_id], 'away', value.trim());
  };

  const markHouse = async (dormId, names) => {
    const ok = await Swal.fire({
      title: 'ทั้งหลังมาครบ?',
      html: `ยืนยันว่าเห็นตัวครบ ${names.length} คน<br/><span class="fs-sm">${names.join(', ')}</span>`,
      showCancelButton: true, confirmButtonText: 'ครบแล้ว', cancelButtonText: 'ยกเลิก',
    });
    if (!ok.isConfirmed) return;
    try {
      await axiosInstance.post(`${API}/safety/emergency/${evId}/houses/${dormId}/safe`);
      load();
    } catch (e) {
      Swal.fire('บันทึกไม่สำเร็จ', apiError(e), 'error');
    }
  };

  const onCard = useCallback(async (uid, { valid }) => {
    if (!valid) { setLastScan({ tone: 'danger', text: 'อ่านบัตรไม่ได้ ลองแตะใหม่' }); return; }
    try {
      const { data: r } = await axiosInstance.post(`${API}/safety/emergency/${evId}/scan`, { uid });
      if (r.notInEvent) setLastScan({ tone: 'warning', student: r.student, text: 'ไม่อยู่ในรายชื่อของเหตุนี้ (อยู่นอกเขตที่ประกาศ)' });
      else if (r.alreadySafe) setLastScan({ tone: 'secondary', student: r.student, text: 'ยืนยันไปแล้ว' });
      else {
        setLastScan({ tone: 'success', student: r.student, text: `ปลอดภัย · ${r.student.village_name} หลัง ${r.student.dorm_name}` });
        setLocal([r.student.st_id], 'safe');
      }
      if (navigator.vibrate) navigator.vibrate(r.notInEvent ? [80, 60, 80] : 60);
    } catch (e) {
      setLastScan({ tone: 'danger', text: apiError(e) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evId]);
  const nfc = useNfcReader(onCard);
  const nfcReason = nfcUnavailableReason();

  const closeEvent = async () => {
    const waiting = data.students.filter((s) => s.status === 'waiting').length;
    const { isConfirmed, value } = await Swal.fire({
      icon: waiting ? 'warning' : 'question',
      title: 'ปิดเหตุ?',
      html: waiting ? `<span class="text-danger">ยังไม่พบอีก ${waiting} คน</span> — ต้องระบุเหตุผล` : 'นับครบทุกคนแล้ว',
      input: 'textarea',
      inputPlaceholder: 'สรุปเหตุการณ์ / เหตุผลที่ปิด',
      inputValidator: (v) => (waiting && !v?.trim() ? 'ต้องระบุเหตุผล' : undefined),
      showCancelButton: true, confirmButtonText: 'ปิดเหตุ', cancelButtonText: 'ยกเลิก',
    });
    if (!isConfirmed) return;
    try {
      await axiosInstance.post(`${API}/safety/emergency/${evId}/close`, { note: value });
      load();
    } catch (e) {
      Swal.fire('ปิดเหตุไม่สำเร็จ', apiError(e), 'error');
    }
  };

  const visible = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.students.filter((s) => {
      if (village && s.village_id !== village) return false;
      if (needle) return s.st_name.toLowerCase().includes(needle) || String(s.st_idnumber).includes(needle);
      if (filter === 'all') return true;
      if (filter === 'excused') return s.status === 'away' || s.status === 'nurse';
      return s.status === filter;
    });
  }, [data, filter, village, q]);

  // จัดเป็น เขต → หลัง เพราะครูที่จุดรวมพลนับกันเป็นกลุ่มหลัง
  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of visible) {
      const vk = s.village_name;
      if (!map.has(vk)) map.set(vk, new Map());
      const houses = map.get(vk);
      if (!houses.has(s.dorm_id)) houses.set(s.dorm_id, { dorm_id: s.dorm_id, dorm_name: s.dorm_name, students: [] });
      houses.get(s.dorm_id).students.push(s);
    }
    return [...map.entries()];
  }, [visible]);

  if (!data) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  const { event } = data;
  const counts = data.students.reduce((c, s) => ({ ...c, [s.status]: (c[s.status] || 0) + 1 }), {});
  const waiting = counts.waiting || 0;

  return (
    <>
      <Card className={`card-one mb-2 ${active ? 'border-danger' : ''}`}>
        <Card.Body className="p-3">
          <div className="d-flex align-items-start gap-2">
            <div className="flex-fill">
              <div className="fw-semibold fs-5">
                {active && <Badge bg="danger" className="me-2">กำลังดำเนินการ</Badge>}
                {event.type_th} · {event.ev_title}
              </div>
              <div className="fs-sm text-secondary">
                {event.ev_scope_label} · ประกาศ {clock(event.ev_started_at)} น. โดย {event.started_by_name}
                {active && ` · ผ่านไป ${minutesSince(event.ev_started_at)} นาที`}
              </div>
              {event.ev_assembly && <div className="fs-sm"><i className="ri-map-pin-2-line me-1"></i>จุดรวมพล: <b>{event.ev_assembly}</b></div>}
              {!active && (
                <div className="fs-sm text-secondary">ปิดเหตุ {toThaiDateLong(event.ev_closed_at, { short: true })} {clock(event.ev_closed_at)} น. โดย {event.closed_by_name}{event.ev_note ? ` — ${event.ev_note}` : ''}</div>
              )}
            </div>
            {onBack && <Button size="sm" variant="outline-secondary" onClick={onBack}><i className="ri-arrow-left-line"></i></Button>}
          </div>
          <div className="d-flex gap-2 mt-3">
            <Counter value={waiting} label="ยังไม่พบ" variant={waiting ? 'danger' : 'success'} big />
            <Counter value={counts.safe || 0} label="ปลอดภัย" variant="success" />
            <Counter value={(counts.away || 0) + (counts.nurse || 0)} label="ไม่อยู่ในหอ / ห้องพยาบาล" variant="secondary" />
          </div>
          {active && data.access.declare && (
            <Button variant="outline-danger" size="sm" className="mt-2" onClick={closeEvent}>ปิดเหตุ</Button>
          )}
        </Card.Body>
      </Card>

      {canMark && (
        <Card className="card-one mb-2">
          <Card.Body className="p-2">
            {nfcReason ? (
              <div className="fs-xs text-secondary mb-1"><i className="ri-rfid-line me-1"></i>แตะบัตรไม่ได้ในเครื่องนี้ ({nfcReason}) — กดชื่อแทน</div>
            ) : (
              <Button className="w-100 mb-2" variant={nfc.scanning ? 'success' : 'outline-success'}
                onClick={() => (nfc.scanning ? nfc.stop() : nfc.start())}>
                <i className="ri-rfid-line me-1"></i>{nfc.scanning ? 'กำลังรอแตะบัตร… (กดเพื่อหยุด)' : 'เริ่มแตะบัตรนักเรียน'}
              </Button>
            )}
            {nfc.error && <div className="text-danger fs-sm">{nfc.error}</div>}
            {lastScan && (
              <Alert variant={lastScan.tone} className="py-2 mb-2 d-flex align-items-center gap-2">
                {lastScan.student && <Avatar photo={lastScan.student.st_photo} name={lastScan.student.st_name} size={40} />}
                <div>
                  {lastScan.student && <div className="fw-semibold">{lastScan.student.st_name}</div>}
                  <div className="fs-sm">{lastScan.text}</div>
                </div>
              </Alert>
            )}
            <Form.Control value={q} onChange={(e) => setQ(e.target.value)} placeholder="ค้นชื่อ / เลขประจำตัว (ค้นจากทุกสถานะ)" />
          </Card.Body>
        </Card>
      )}

      {data.villages.length > 1 && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          <Button size="sm" variant={village === null ? 'dark' : 'outline-dark'} onClick={() => setVillage(null)}>ทุกเขต</Button>
          {data.villages.map((v) => {
            const left = data.students.filter((s) => s.village_id === v.village_id && s.status === 'waiting').length;
            return (
              <Button key={v.village_id} size="sm" variant={village === v.village_id ? 'dark' : (left ? 'outline-danger' : 'outline-success')}
                onClick={() => setVillage(village === v.village_id ? null : v.village_id)}>
                {v.village_name} {left ? <Badge bg="danger">{left}</Badge> : <i className="ri-check-line"></i>}
              </Button>
            );
          })}
        </div>
      )}

      {!q && (
        <ButtonGroup size="sm" className="mb-2 w-100">
          {FILTERS.map((f) => (
            <Button key={f.key} variant={filter === f.key ? 'primary' : 'outline-primary'} onClick={() => setFilter(f.key)}>{f.label}</Button>
          ))}
        </ButtonGroup>
      )}

      {grouped.length === 0 ? (
        <div className="text-center text-secondary py-4">
          <i className="ri-checkbox-circle-line d-block mb-2 text-success" style={{ fontSize: 40 }}></i>
          {filter === 'waiting' && !q ? 'พบครบทุกคนแล้ว' : 'ไม่มีรายชื่อ'}
        </div>
      ) : grouped.map(([villageName, houses]) => (
        <Card key={villageName} className="card-one mb-2">
          <Card.Header className="py-2 fw-semibold">{villageName}</Card.Header>
          {[...houses.values()].map((h) => {
            const houseWaiting = h.students.filter((s) => s.status === 'waiting');
            return (
              <div key={h.dorm_id}>
                <div className="px-3 py-1 bg-secondary bg-opacity-10 d-flex align-items-center">
                  <span className="flex-fill fs-sm">หลัง {h.dorm_name}</span>
                  {canMark && houseWaiting.length > 0 && (
                    <Button size="sm" variant="outline-success" className="py-0"
                      onClick={() => markHouse(h.dorm_id, houseWaiting.map((s) => s.st_name))}>
                      ทั้งหลังมาครบ
                    </Button>
                  )}
                </div>
                <ListGroup variant="flush">
                  {h.students.map((s) => (
                    <ListGroup.Item key={s.st_id} className="d-flex align-items-center gap-2 py-2">
                      <Avatar photo={s.st_photo} name={s.st_name} />
                      <div className="flex-fill min-width-0">
                        <div className="text-truncate">{s.st_name}</div>
                        <div className="fs-xs text-secondary">
                          {s.st_idnumber}
                          {s.reason && <span className={s.status === 'waiting' ? 'text-danger' : ''}> · {s.reason}</span>}
                          {s.status === 'safe' && s.at && ` · ${clock(s.at)} ${s.method === 'card' ? 'แตะบัตร' : s.method === 'house' ? 'ยืนยันทั้งหลัง' : 'กดชื่อ'} ${s.by_name || ''}`}
                        </div>
                      </div>
                      {s.status === 'waiting' && canMark && (
                        <>
                          <Button size="sm" variant="success" onClick={() => mark([s.st_id], 'safe')}>มาแล้ว</Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => markAway(s)} title="ไม่อยู่ในหอ">
                            <i className="ri-home-4-line"></i>
                          </Button>
                        </>
                      )}
                      {s.status === 'safe' && (
                        canMark
                          ? <Button size="sm" variant="outline-secondary" onClick={() => mark([s.st_id], 'waiting')}>ยกเลิก</Button>
                          : <Badge bg="success">ปลอดภัย</Badge>
                      )}
                      {(s.status === 'away' || s.status === 'nurse') && (
                        <>
                          <Badge bg={s.status === 'nurse' ? 'info' : 'secondary'}>{s.status === 'nurse' ? 'ห้องพยาบาล' : 'ไม่อยู่ในหอ'}</Badge>
                          {canMark && <Button size="sm" variant="outline-success" onClick={() => mark([s.st_id], 'safe')}>มาแล้ว</Button>}
                        </>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            );
          })}
        </Card>
      ))}
    </>
  );
};

export default function Emergency() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const viewEv = Number(params.get('ev')) || null;

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`${API}/safety/status`);
      setStatus(data);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // มีคนประกาศ/ปิดเหตุจากเครื่องอื่น — หน้านี้ต้องสลับเองโดยไม่ต้องรีเฟรช
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } });
    socket.on('emergency:update', loadStatus);
    return () => socket.disconnect();
  }, [loadStatus]);

  const evId = viewEv || status?.active?.ev_id || null;

  let body;
  if (error) body = <Alert variant="danger">{error}</Alert>;
  else if (!status) body = <div className="text-center py-5"><Spinner animation="border" /></div>;
  else if (!status.emergency.view) body = <Alert variant="warning">ไม่มีสิทธิ์ใช้งานหน้านี้</Alert>;
  else if (evId) body = <Board key={evId} evId={evId} onBack={viewEv ? () => setParams({}) : null} />;
  else body = (
    <>
      {status.emergency.declare
        ? <DeclareForm onStarted={() => loadStatus()} />
        : <Alert variant="light">ตอนนี้ไม่มีเหตุฉุกเฉิน — เมื่อหัวหน้าฝ่ายปกครองประกาศเหตุ หน้านี้จะขึ้นรายชื่อให้นับทันที</Alert>}
      <History onOpen={(id) => setParams({ ev: String(id) })} />
    </>
  );

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">เหตุฉุกเฉิน / นับยอดนักเรียน</h4>
          <div className="fs-xs text-secondary">ประกาศเหตุ แล้วนับที่จุดรวมพลจน "ยังไม่พบ" เป็นศูนย์</div>
        </div>
        {body}
        <Footer />
      </div>
    </>
  );
}
